import React, { useEffect, useMemo, useState } from "react";
import { Crown, LayoutGrid, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import { EngineRow } from "@/components/nds/windows/resident/access/EngineRows";
import { Btn, Modal } from "@/components/console/kit";

const useIsSa = () => { const { user } = useAuth(); return user?.role === "super_admin" || !!user?.is_super_admin; };

/** Super Admin: pick exactly which cards a Resident Leader can open on their desktop. */
export const LeaderCardsDialog = ({ rid, name, onClose }) => {
  const [cat, setCat] = useState(null);
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    Promise.all([apiClient.get("/access/catalog"), apiClient.get(`/access/person/leader/${rid}`)])
      .then(([c, p]) => { setCat(c.data); setAccess(p.data.card_access || {}); }).catch((e) => toast.error(formatApiError(e)));
  }, [rid]);
  const engines = useMemo(() => (cat?.engines || []).map((e) => ({ ...e, cards: e.cards.filter((c) => !c.sa_only) })).filter((e) => e.cards.length), [cat]);
  const set = (id, lv) => setAccess((d) => { const n = { ...d }; if (lv) n[id] = lv; else delete n[id]; return n; });
  const setMany = (ids, lv, isSee) => setAccess((d) => { const n = { ...d }; ids.forEach((id) => { if (!lv) delete n[id]; else if (isSee) n[id] = n[id] || "view"; else n[id] = lv; }); return n; });
  const save = async () => {
    setBusy(true);
    try { await apiClient.put(`/access/person/leader/${rid}`, { card_access: access }); toast.success(`${name}'s cards saved`); onClose(); }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  return (
    <Modal open title={`Leader cards · ${name}`} onClose={onClose} testId="leader-cards-dialog">
      <p className="text-xs text-white/50 mb-3" data-testid="leader-cards-count">{(() => { const n = access ? Object.keys(access).length : 0; return `${n} card${n === 1 ? "" : "s"}`; })()} granted · they see only these on their desktop, and every resident except themselves</p>
      {!cat || !access ? <div className="text-sm text-white/50"><Loader2 size={14} className="inline animate-spin mr-2" />Loading cards…</div>
        : <div className="max-h-[55vh] overflow-y-auto space-y-1.5 pr-1">{engines.map((e) => <EngineRow key={e.id} engine={e} access={access} set={set} setMany={setMany} locked={false} />)}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <Btn tone="muted" onClick={onClose} data-testid="leader-cards-cancel">Cancel</Btn>
        <Btn onClick={save} disabled={busy || !access} data-testid="leader-cards-save">{busy ? "Saving…" : "Save cards"}</Btn>
      </div>
    </Modal>
  );
};

/** Super Admin only: make a resident a Resident Leader (same phone + PIN), step them back, and open their cards. */
export const ResidentLeaderControls = ({ rid, name, leader, onChanged, compact = false }) => {
  const isSa = useIsSa();
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState(false);
  if (!isSa) return leader ? <LeaderBadge /> : null;
  const toggle = async () => {
    if (leader && !window.confirm(`Step ${name} back to a regular resident? Their leader cards stop working right away.`)) return;
    setBusy(true);
    try { await apiClient.put(`/access/leader/${rid}`, { on: !leader }); toast.success(leader ? `${name} is a regular resident again` : `${name} is now a Resident Leader — pick their cards`); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <span className="inline-flex items-center gap-2" data-testid={`leader-controls-${rid}`}>
      <Btn tone="muted" onClick={toggle} disabled={busy} data-testid={`leader-toggle-${rid}`}><Crown size={12} /> {leader ? (compact ? "Remove leader" : "Remove Resident Leader") : (compact ? "Make leader" : "Make Resident Leader")}</Btn>
      {leader && <Btn tone="muted" onClick={() => setCards(true)} data-testid={`leader-cards-${rid}`}><LayoutGrid size={12} /> Cards</Btn>}
      {cards && <LeaderCardsDialog rid={rid} name={name} onClose={() => setCards(false)} />}
    </span>
  );
};

export const LeaderBadge = () => (
  <span data-testid="leader-badge" className="inline-flex items-center gap-1 rounded-full border border-[#d4a017]/50 bg-[#d4a017]/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] text-[#d4a017]"><Crown size={10} /> Resident Leader</span>
);
